import React, { useState } from 'react'
import get from 'lodash/fp/get'
import pick from 'lodash/fp/pick'
import flow from 'lodash/fp/flow'
import set from 'lodash/fp/set'
import replace from 'lodash/fp/replace'
import kebabCase from 'lodash/fp/kebabCase'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'
import Container from 'react-bootstrap/Container'
import Form from 'react-bootstrap/Form'
import Alert from 'react-bootstrap/Alert'
import Modal from 'react-bootstrap/Modal'
import { useMutation } from '@apollo/client'
import { useHistory } from 'react-router-dom'
import { MdOutlineContentCopy, MdModeEdit } from 'react-icons/md'

import {
  IGenericErrorObject,
  MyEvent,
  Event,
  Error,
  Maybe,
  GetAllEventsQuery,
  ISettingsCard,
} from '../../../../types'
import { useForm } from '../../../../common/alterForms'
import { simpleAlert, useImageUpload } from '../../../../common'
import { createImageUrl } from '../../../../common/imageFunctions'
import { KnTField, InputSwitch, Textarea } from '../../../../components'
import { ImageCropModal } from '../../../../components/ImageCropModal'
import { UPDATE_EVENT, DELETE_EVENT, GET_ALL_EVENTS } from '../../../../graphql'
import defaultPicture from '../../../../assets/images/rsvp/user.jpg'
import { APP_URL, myHub } from '../../../../common/constants'
import { useAppDispatch } from '../../../../hooks'
import { updateImageVersion } from '../../../../features/siteSlice'

export const SettingsCard: React.FC<ISettingsCard> = ({
  event,
  setPreviewHelper,
  previewHelper,
}) => {
  const history = useHistory()
  const dispatch = useAppDispatch()
  const [showMsg, setShowMsg] = useState(false)
  const [showError, setShowError] = useState(false)
  const [copied, setCopied] = useState(false)
  const [isPrivate, setIsPrivate] = useState(() => event.isPrivate)
  const [showDialog, setShowDialog] = useState(false)
  const [dialogError, setDialogError] = useState('')
  const [deleteEventName, setDeleteEventName] = useState('')

  const mainImageUpload = useImageUpload({
    path: `events/${event.id}`,
    name: 'main',
    aspectRatio: 1,
    onSuccess: () => {
      dispatch(updateImageVersion())
      setPreviewHelper(previewHelper + 1)
    },
  })

  const coverImageUpload = useImageUpload({
    path: `events/${event.id}`,
    name: 'cover',
    aspectRatio: 16 / 9,
    onSuccess: () => {
      dispatch(updateImageVersion())
      setPreviewHelper(previewHelper + 1)
    },
  })

  const coupleImageUpload = useImageUpload({
    path: `events/${event.id}`,
    name: 'couple',
    aspectRatio: 3 / 4,
    onSuccess: () => {
      dispatch(updateImageVersion())
      setPreviewHelper(previewHelper + 1)
    },
  })

  const { model, errors, handleFieldChange, handleCheckboxChange, pushError } =
    useForm<
      Pick<
        MyEvent,
        | 'name'
        | 'defaultKidsAllowed'
        | 'defaultPlusOne'
        | 'description'
        | 'emailCustomText'
        | 'customUrl'
        | 'brideFirstName'
        | 'brideLastName'
        | 'groomFirstName'
        | 'groomLastName'
      >
    >(
      pick(
        [
          'name',
          'defaultKidsAllowed',
          'defaultPlusOne',
          'description',
          'emailCustomText',
          'customUrl',
          'brideFirstName',
          'brideLastName',
          'groomFirstName',
          'groomLastName',
        ],
        event
      )
    )
  const [updateEvent, { loading }] = useMutation(UPDATE_EVENT, {
    onError: () => {
      simpleAlert({
        html: 'Unauthorized access',
        icon: 'error',
      })
    },
  })
  const [handleDeleteEvent, { loading: loadingDelete }] = useMutation(
    DELETE_EVENT,
    {
      update(cache, { data: { deleteEvent } }) {
        if (deleteEvent.errors.length === 0) {
          const cached: Maybe<GetAllEventsQuery> = cache.readQuery({
            query: GET_ALL_EVENTS,
          })
          if (cached) {
            const myEvents = get('me.myEventList', cached) || []
            const otherEvents = get('me.eventList', cached) || []

            const myEventsFiltered = myEvents.filter(
              (ev: MyEvent) => ev.id !== deleteEvent.result.id
            )
            const otherEventsFiltered = otherEvents.filter(
              (ev: Event) => ev.id !== deleteEvent.result.id
            )

            const me = flow(
              set('me.myEventList', myEventsFiltered),
              set('me.eventList', otherEventsFiltered)
            )(cached)

            cache.writeQuery({
              query: GET_ALL_EVENTS,
              data: me,
            })
          }
        }
      },
      onError: () => {
        // in case a user different from :admin or :concierge tries to create an event
        simpleAlert({
          html: 'Unauthorized access',
          icon: 'error',
        })
      },
    }
  )

  const handleSubmit = (ev: React.FormEvent<HTMLFormElement>) => {
    ev.preventDefault()
    const variables = {
      input: {
        ...model,
        isPrivate,
        customUrl: flow(
          replace(/[\\/]/g, ''),
          kebabCase
        )(model.customUrl as string),
      },
      id: event.id,
    }
    updateEvent({ variables }).then(({ data, errors: errorsGraphql }) => {
      if (errorsGraphql) return
      if (data?.updateEvent?.errors.length > 0) {
        data.updateEvent.errors.forEach(({ key, message }: Error) => {
          pushError(message, key)
        })
        setShowError(true)
        setTimeout(() => setShowError(false), 4000)
        return
      }

      setShowMsg(true)
      setTimeout(() => setShowMsg(false), 4000)
      setPreviewHelper(previewHelper + 1)
    })
  }

  const callDeleteEvent = (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault()
    if (deleteEventName !== model.name) {
      setDialogError('It seems that the name is not correct...')
    } else {
      const variables = { id: event.id }
      handleDeleteEvent({ variables })
        .then(({ data }) => {
          if (data.deleteEvent.errors.length > 0) {
            data.deleteEvent.errors.forEach(({ message }: Error) =>
              setDialogError(message)
            )
          } else {
            setDeleteEventName('')
            setDialogError('')
            history.push(myHub)
          }
        })
        .catch(() => {
          setDialogError('Something went wrong, please try again later')
        })
    }
  }

  const displayImage = () => {
    if (mainImageUpload.imagePreview) return mainImageUpload.imagePreview
    if (mainImageUpload.imageLoadError) return defaultPicture
    return createImageUrl({
      path: `events/${event.id}/main.jpg`,
      size: '300x300',
    })
  }

  const displayCoverImage = () => {
    if (coverImageUpload.imagePreview) return coverImageUpload.imagePreview
    if (coverImageUpload.imageLoadError) return defaultPicture
    return createImageUrl({
      path: `events/${event.id}/cover.jpg`,
      size: '800x450',
    })
  }

  const displayCoupleImage = () => {
    if (coupleImageUpload.imagePreview) return coupleImageUpload.imagePreview
    if (coupleImageUpload.imageLoadError) return defaultPicture
    return createImageUrl({
      path: `events/${event.id}/couple.jpg`,
      size: '450x600',
    })
  }

  return (
    <Container>
      <Modal show={showDialog} onHide={() => setShowDialog(false)}>
        <Modal.Body>
          <h5 className="font-weight-bold">Watch out!</h5>
          <p>
            this action is irreversible, enter the name of the event to continue
          </p>
          <input
            placeholder="Event name..."
            type="text"
            className="form-control"
            name="delete-my-event"
            onChange={ev => setDeleteEventName(ev.currentTarget.value)}
          />
          {dialogError && (
            <Form.Text className="text-muted">{dialogError}</Form.Text>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            disabled={loadingDelete}
            onClick={callDeleteEvent}
            variant="primary"
          >
            Delete it
          </Button>
          <Button onClick={() => setShowDialog(false)} variant="secondary">
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <Form onSubmit={handleSubmit} className="pt-4">
        <Card className="mt-3">
          <Card.Body>
            <div className="d-flex flex-wrap mb-4">
              <div className="px-2 w-fit-content mx-auto">
                <h6 className="font-weight-bold mb-3">Main Event Image</h6>
                <div className="text-center">
                  <img
                    src={displayImage()}
                    alt={model.name}
                    style={{ maxWidth: '200px', height: 'auto' }}
                    onError={() => mainImageUpload.setImageLoadError(true)}
                  />
                  <input
                    type="file"
                    accept="image/jpeg,image/png"
                    onChange={mainImageUpload.handleFileSelect}
                    style={{ display: 'none' }}
                    id="main-image-upload"
                    disabled={mainImageUpload.uploading}
                  />
                  <div className="mt-3">
                    <Button
                      as="label"
                      htmlFor="main-image-upload"
                      variant="outline-secondary"
                      disabled={mainImageUpload.uploading}
                    >
                      <MdModeEdit size={24} className="mr-1" />
                      {mainImageUpload.uploading
                        ? 'Uploading...'
                        : 'Edit Image'}
                    </Button>
                  </div>
                  {mainImageUpload.uploadImageMsg && (
                    <span className="d-block fz-11 text-primary mt-2">
                      {mainImageUpload.uploadImageMsg}
                    </span>
                  )}
                </div>
              </div>

              <div className="px-2 w-fit-content mx-auto">
                <h6 className="font-weight-bold mb-3">Cover Image</h6>
                <div className="text-center">
                  <img
                    src={displayCoverImage()}
                    alt={`${model.name} cover`}
                    style={{ maxWidth: '300px', height: 'auto' }}
                    onError={() => coverImageUpload.setImageLoadError(true)}
                  />
                  <input
                    type="file"
                    accept="image/jpeg,image/png"
                    onChange={coverImageUpload.handleFileSelect}
                    style={{ display: 'none' }}
                    id="cover-image-upload"
                    disabled={coverImageUpload.uploading}
                  />
                  <div className="mt-3">
                    <Button
                      as="label"
                      htmlFor="cover-image-upload"
                      variant="outline-secondary"
                      disabled={coverImageUpload.uploading}
                    >
                      <MdModeEdit size={24} className="mr-1" />
                      {coverImageUpload.uploading
                        ? 'Uploading...'
                        : 'Edit Cover'}
                    </Button>
                  </div>
                  {coverImageUpload.uploadImageMsg && (
                    <span className="d-block fz-11 text-primary mt-2">
                      {coverImageUpload.uploadImageMsg}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <hr />

            <KnTField
              error={errors}
              value={model}
              setValue={handleFieldChange}
              name="name"
              label="Event Name"
            />

            <Row>
              <Col xs={12} md={6}>
                <KnTField
                  error={errors}
                  value={model}
                  setValue={handleFieldChange}
                  name="brideFirstName"
                  label="Partner 1 First Name"
                  required
                  placeholder="First name"
                />
              </Col>
              <Col xs={12} md={6}>
                <KnTField
                  error={errors}
                  value={model}
                  setValue={handleFieldChange}
                  name="brideLastName"
                  label="Partner 1 Last Name"
                  required={false}
                  placeholder="Last name (optional)"
                />
              </Col>
            </Row>

            <Row>
              <Col xs={12} md={6}>
                <KnTField
                  error={errors}
                  value={model}
                  setValue={handleFieldChange}
                  name="groomFirstName"
                  label="Partner 2 First Name"
                  required
                  placeholder="First name"
                />
              </Col>
              <Col xs={12} md={6}>
                <KnTField
                  error={errors}
                  value={model}
                  setValue={handleFieldChange}
                  name="groomLastName"
                  label="Partner 2 Last Name"
                  required={false}
                  placeholder="Last name (optional)"
                />
              </Col>
            </Row>
            <KnTField
              error={errors}
              value={model}
              setValue={handleFieldChange}
              name="customUrl"
              label="Custom URL"
              required
              placeholder="e.g. my-event-2022 or my birthday party"
            />
            <span className="px-3 py-1 d-block">
              {APP_URL}/invite/{event.customUrl || '{your custom url}'}
              {event.customUrl && (
                <>
                  <MdOutlineContentCopy
                    size={16}
                    className="cursor ml-2"
                    title="copy to clipboard"
                    onClick={() => {
                      navigator.clipboard.writeText(
                        `${APP_URL}/invite/${event.customUrl}`
                      )
                      setCopied(true)
                      setTimeout(() => setCopied(false), 2000)
                    }}
                  />
                  {copied && <span className="ml-2">Copied!</span>}
                </>
              )}
            </span>
            <Textarea
              rows={5}
              required={false}
              error={errors as IGenericErrorObject}
              value={model.description}
              change={handleFieldChange}
              name="description"
              label="Personalize a welcome message to your guests"
              className="custom-input-style-textarea"
              placeholder="This is the initial message sent to all guests and can change as your event planning and details progress. Feel free to address your guests with a personal message here."
            />
            <Textarea
              rows={5}
              required={false}
              error={errors as IGenericErrorObject}
              value={model.emailCustomText}
              change={handleFieldChange}
              name="emailCustomText"
              label="Personalize an email invite to your guests"
              className="custom-input-style-textarea"
            />

            <Row>
              <Col xs={12} md={6}>
                <KnTField
                  type="number"
                  error={errors}
                  value={model}
                  setValue={handleFieldChange}
                  valueType="INTEGER"
                  name="defaultPlusOne"
                  label="How many +1s allowed?"
                  className="custom-input-style-simple"
                  attrs={{ min: 0 }}
                />
                <InputSwitch
                  value={!!model.defaultKidsAllowed}
                  change={handleCheckboxChange}
                  name="defaultKidsAllowed"
                  label="Kids allowed by default?"
                />
              </Col>
              <Col xs={12} md={6}>
                <Form.Check
                  type="radio"
                  label="Private event"
                  name="isPrivate"
                  id="private-event"
                  checked={isPrivate}
                  className="mt-2 mt-md-4 fwsb fz-14 custom-radio"
                  onChange={() => setIsPrivate(true)}
                />

                <Form.Check
                  type="radio"
                  label="Public event"
                  name="isPrivate"
                  id="public-event"
                  checked={!isPrivate}
                  className="mt-2 mt-md-4 fwsb fz-14 custom-radio"
                  onChange={() => setIsPrivate(false)}
                />
              </Col>
            </Row>

            <hr />

            <div className="d-flex justify-content-center mb-4">
              <div className="px-2 w-fit-content">
                <h6 className="font-weight-bold mb-3 text-center">
                  Invitation Image
                </h6>
                <div className="text-center">
                  <img
                    src={displayCoupleImage()}
                    alt={`${model.name} invitation`}
                    style={{ maxWidth: '250px', height: 'auto' }}
                    onError={() => coupleImageUpload.setImageLoadError(true)}
                  />
                  <input
                    type="file"
                    accept="image/jpeg,image/png"
                    onChange={coupleImageUpload.handleFileSelect}
                    style={{ display: 'none' }}
                    id="couple-image-upload"
                    disabled={coupleImageUpload.uploading}
                  />
                  <div className="mt-3">
                    <Button
                      as="label"
                      htmlFor="couple-image-upload"
                      variant="outline-secondary"
                      disabled={coupleImageUpload.uploading}
                    >
                      <MdModeEdit size={24} className="mr-1" />
                      {coupleImageUpload.uploading
                        ? 'Uploading...'
                        : 'Edit Invitation Image'}
                    </Button>
                  </div>
                  {coupleImageUpload.uploadImageMsg && (
                    <span className="d-block fz-11 text-primary mt-2">
                      {coupleImageUpload.uploadImageMsg}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {showMsg && (
              <Alert variant="success" className="fz-13">
                Settings updated successfully
              </Alert>
            )}
            {showError && (
              <Alert variant="danger" className="fz-13">
                It was not possible to update the settings
              </Alert>
            )}
          </Card.Body>
          <Card.Footer className="text-right">
            <Button
              onClick={() => setShowDialog(true)}
              className="fz-14 fwsb float-left"
              variant="dark"
            >
              Delete Event
            </Button>
            <Button
              disabled={loading}
              type="submit"
              variant="primary"
              className="fz-14 fwsb"
            >
              Save changes
            </Button>
          </Card.Footer>
        </Card>
      </Form>
      {mainImageUpload.selectedImage && (
        <ImageCropModal
          show={mainImageUpload.showCropModal}
          imageSrc={mainImageUpload.selectedImage}
          onHide={() => {
            mainImageUpload.setShowCropModal(false)
            mainImageUpload.setSelectedImage(null)
          }}
          onCropComplete={mainImageUpload.handleCropComplete}
          aspectRatio={mainImageUpload.aspectRatio}
        />
      )}
      {coverImageUpload.selectedImage && (
        <ImageCropModal
          show={coverImageUpload.showCropModal}
          imageSrc={coverImageUpload.selectedImage}
          onHide={() => {
            coverImageUpload.setShowCropModal(false)
            coverImageUpload.setSelectedImage(null)
          }}
          onCropComplete={coverImageUpload.handleCropComplete}
          aspectRatio={coverImageUpload.aspectRatio}
        />
      )}
      {coupleImageUpload.selectedImage && (
        <ImageCropModal
          show={coupleImageUpload.showCropModal}
          imageSrc={coupleImageUpload.selectedImage}
          onHide={() => {
            coupleImageUpload.setShowCropModal(false)
            coupleImageUpload.setSelectedImage(null)
          }}
          onCropComplete={coupleImageUpload.handleCropComplete}
          aspectRatio={coupleImageUpload.aspectRatio}
        />
      )}
    </Container>
  )
}
